import { Component, EventEmitter, Input, Output } from '@angular/core';

export interface BarDatum { label: string; value: number; }
export interface BarRect {
  label: string; index: number;
  x: number; y: number; w: number; h: number;
}
export interface BarLayout {
  bars: BarRect[];
  averageY: number | null;
  baselineY: number;
}

export const BAR_VIEW_W = 240;
export const BAR_VIEW_H = 108;
/**
 * The plot starts below a reserved band, not at the top of the viewBox.
 *
 * The average legend lives in that band rather than floating on the average
 * line, because the line sits wherever the average falls — and on a steady
 * month-to-month spend that is exactly where every bar is tallest. The bars
 * paint after it, so the figure was being sliced through by the first two
 * columns and was unreadable on the real ledger.
 */
export const BAR_TOP_Y = 22;
export const BAR_BASELINE_Y = 84;
export const BAR_LABEL_Y = 98;
/** Baseline of the average legend, inside the reserved band above the plot. */
export const BAR_AVERAGE_BAND_Y = 9;
/** The dashed swatch that ties the legend to the line it describes. */
export const BAR_SWATCH_X1 = 2;
export const BAR_SWATCH_X2 = 14;
export const BAR_AVERAGE_TEXT_X = 18;
/** Fraction of each slot the bar occupies; the rest is gutter. */
const BAR_FILL = 0.62;

/**
 * Values to rectangles. Pure.
 *
 * The scale spans the tallest bar AND the average line, so an average above
 * every bar still lands inside the plot rather than floating off the top.
 * Negative values clamp to the baseline instead of drawing upside down.
 */
export function barLayout(data: BarDatum[], averageValue: number | null = null): BarLayout {
  const rows = data ?? [];
  if (rows.length === 0) {
    return { bars: [], averageY: null, baselineY: BAR_BASELINE_Y };
  }

  const plotH = BAR_BASELINE_Y - BAR_TOP_Y;
  const values = rows.map(r => Math.max(0, r.value));
  const max = Math.max(...values, averageValue ?? 0);

  const slot = BAR_VIEW_W / rows.length;
  const w = slot * BAR_FILL;

  const bars = rows.map((r, index) => {
    const h = max > 0 ? (Math.max(0, r.value) / max) * plotH : 0;
    return {
      label: r.label, index,
      x: index * slot + (slot - w) / 2,
      y: BAR_BASELINE_Y - h,
      w, h,
    };
  });

  const averageY = averageValue !== null && max > 0
    ? BAR_BASELINE_Y - (averageValue / max) * plotH
    : null;

  return { bars, averageY, baselineY: BAR_BASELINE_Y };
}

@Component({
  selector: 'mm-bars',
  templateUrl: './bar-chart.component.html',
  styleUrls: ['./bar-chart.component.scss'],
  standalone: false,
})
export class BarChartComponent {
  @Input() data: BarDatum[] = [];
  @Input() averageValue: number | null = null;
  /** Pre-formatted — charts never format money. */
  @Input() averageLabel = '';
  @Input() highlightIndex: number | null = null;
  @Input() ariaLabel = 'Spending by month';
  /** Pre-formatted display values, parallel to `data`, for the a11y table. */
  @Input() displayValues: string[] = [];

  @Output() select = new EventEmitter<number>();

  readonly viewW = BAR_VIEW_W;
  readonly viewH = BAR_VIEW_H;
  readonly labelY = BAR_LABEL_Y;
  readonly bandY = BAR_AVERAGE_BAND_Y;
  readonly swatchX1 = BAR_SWATCH_X1;
  readonly swatchX2 = BAR_SWATCH_X2;
  readonly averageTextX = BAR_AVERAGE_TEXT_X;

  get layout(): BarLayout { return barLayout(this.data, this.averageValue); }

  trackBar(_: number, bar: BarRect): number { return bar.index; }

  // preventDefault: Space on a focused element scrolls the page as well as
  // activating it.
  onSelect(bar: BarRect, event?: Event): void {
    event?.preventDefault();
    this.select.emit(bar.index);
  }
}
